import React, { useState, useRef, useEffect, useCallback, useMemo } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { Dashboard, Doctor, Lab, Store, Farms } from '../../assets/icons';
import '../../assets/css/components/role-switcher.css';

const ROLE_CONFIG = {
    farmer: {
        label: 'Farmer',
        shortLabel: 'Farm',
        icon: Farms,
        dashboard: '/cases',
        color: '#10b981',
        description: 'Manage your farm cases'
    },
    doctor: {
        label: 'Veterinarian',
        shortLabel: 'Vet',
        icon: Doctor,
        dashboard: '/vet',
        color: '#3b82f6',
        description: 'View assigned cases'
    },
    lab: {
        label: 'Laboratory',
        shortLabel: 'Lab',
        icon: Lab,
        dashboard: '/lab',
        color: '#8b5cf6',
        description: 'Process lab samples'
    },
    vendor: {
        label: 'Vendor',
        shortLabel: 'Shop',
        icon: Store,
        dashboard: '/vendor',
        color: '#f59e0b',
        description: 'Manage your store'
    },
    aht: {
        label: 'Animal Health Technologist',
        shortLabel: 'AHT',
        icon: Dashboard,
        dashboard: '/aht',
        color: '#14b8a6',
        description: 'Manage animal health cases'
    },
    admin: {
        label: 'Administrator',
        shortLabel: 'Admin',
        icon: Dashboard,
        dashboard: '/admin',
        color: '#ef4444',
        description: 'Platform administration'
    }
};

const RoleSwitcher = () => {
    const { getUserRoles } = useAuth();
    const navigate = useNavigate();
    const location = useLocation();
    const [isOpen, setIsOpen] = useState(false);
    const [focusedIndex, setFocusedIndex] = useState(-1);
    const [showTooltip, setShowTooltip] = useState(false);
    const dropdownRef = useRef(null);
    const triggerRef = useRef(null);

    const userRoles = useMemo(() => {
        return getUserRoles ? getUserRoles() : [];
    }, [getUserRoles]);

    // Determine current active role based on URL
    const getCurrentRole = useCallback(() => {
        const path = location.pathname;
        if (path.startsWith('/admin')) return 'admin';
        if (path.startsWith('/vet')) return 'doctor';
        if (path.startsWith('/lab')) return 'lab';
        if (path.startsWith('/vendor')) return 'vendor';
        if (path.startsWith('/cases') || path.startsWith('/aqua-doc')) return 'farmer';
        if (path.startsWith('/aht')) return 'aht';
        return userRoles[0];
    }, [location.pathname, userRoles]);

    const currentRole = getCurrentRole();

    // Handle role switch
    const handleRoleSwitch = useCallback((role) => {
        const roleConfig = ROLE_CONFIG[role];
        if (roleConfig && role !== currentRole) {
            localStorage.setItem('activeRole', role);
            setIsOpen(false);
            setFocusedIndex(-1);
            navigate(roleConfig.dashboard);
        } else {
            setIsOpen(false);
        }
    }, [currentRole, navigate]);

    // Close dropdown when clicking outside
    useEffect(() => {
        const handleClickOutside = (event) => {
            if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
                setIsOpen(false);
                setFocusedIndex(-1);
            }
        };

        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    // Keyboard navigation
    const handleKeyDown = useCallback((e) => {
        if (!isOpen) {
            if (e.key === 'Enter' || e.key === ' ' || e.key === 'ArrowDown') {
                e.preventDefault();
                setIsOpen(true);
                setFocusedIndex(0);
            }
            return;
        }

        switch (e.key) {
            case 'Escape':
                setIsOpen(false);
                setFocusedIndex(-1);
                triggerRef.current?.focus();
                break;
            case 'ArrowDown':
                e.preventDefault();
                setFocusedIndex(prev => 
                    prev < userRoles.length - 1 ? prev + 1 : 0
                );
                break;
            case 'ArrowUp':
                e.preventDefault();
                setFocusedIndex(prev => 
                    prev > 0 ? prev - 1 : userRoles.length - 1
                );
                break;
            case 'Enter':
            case ' ':
                e.preventDefault();
                if (focusedIndex >= 0 && focusedIndex < userRoles.length) {
                    handleRoleSwitch(userRoles[focusedIndex]);
                }
                break;
            case 'Tab':
                setIsOpen(false);
                setFocusedIndex(-1);
                break;
            default:
                break;
        }
    }, [isOpen, focusedIndex, userRoles, handleRoleSwitch]);

    useEffect(() => {
        if (isOpen) {
            document.addEventListener('keydown', handleKeyDown);
            return () => document.removeEventListener('keydown', handleKeyDown);
        }
    }, [isOpen, handleKeyDown]);

    // Don't show if user has only one role or no roles
    if (!userRoles || userRoles.length <= 1) {
        return null;
    }

    const currentRoleConfig = ROLE_CONFIG[currentRole];

    const toggleDropdown = () => {
        setIsOpen(!isOpen);
        if (!isOpen) {
            setFocusedIndex(userRoles.indexOf(currentRole));
        }
    };

    return (
        <div className="role-switcher" ref={dropdownRef}>
            <button 
                ref={triggerRef}
                className={`role-switcher-trigger ${isOpen ? 'open' : ''}`}
                onClick={toggleDropdown}
                onMouseEnter={() => setShowTooltip(true)}
                onMouseLeave={() => setShowTooltip(false)}
                style={{ '--role-color': currentRoleConfig?.color }}
                aria-expanded={isOpen}
                aria-haspopup="listbox"
                aria-label={`Current role: ${currentRoleConfig?.label}. Click to switch dashboard.`}
            >
                <div className="current-role">
                    <div className="role-icon-wrapper" style={{ '--role-color': currentRoleConfig?.color }}>
                        {currentRoleConfig?.icon && (
                            <img 
                                src={currentRoleConfig.icon} 
                                alt="" 
                                className="role-icon"
                            />
                        )}
                    </div>
                    <div className="role-text">
                        <span className="role-label">{currentRoleConfig?.label}</span>
                        <span className="role-hint">Switch role</span>
                    </div>
                </div>
                <div className={`switcher-arrow ${isOpen ? 'up' : 'down'}`}>
                    <svg width="12" height="12" viewBox="0 0 12 12" fill="none">
                        <path d="M2.5 4.5L6 8L9.5 4.5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                    </svg>
                </div>
            </button>

            {/* Tooltip */}
            {showTooltip && !isOpen && (
                <div className="role-tooltip">
                    You have {userRoles.length} roles
                </div>
            )}

            {isOpen && (
                <div 
                    className="role-switcher-dropdown"
                    role="listbox"
                    aria-label="Switch dashboard"
                >
                    <div className="dropdown-header">
                        <div className="header-content">
                            <span className="header-title">Switch Dashboard</span>
                            <span className="header-subtitle">{userRoles.length} roles available</span>
                        </div>
                        <kbd className="keyboard-hint">↑↓ to navigate</kbd>
                    </div>
                    
                    <div className="role-options">
                        {userRoles.map((role, index) => {
                            const roleConfig = ROLE_CONFIG[role];
                            if (!roleConfig) return null;
                            
                            const isActive = role === currentRole;
                            const isFocused = index === focusedIndex;
                            
                            return (
                                <button
                                    key={role}
                                    className={`role-option ${isActive ? 'active' : ''} ${isFocused ? 'focused' : ''}`}
                                    onClick={() => handleRoleSwitch(role)}
                                    onMouseEnter={() => setFocusedIndex(index)}
                                    style={{ '--role-color': roleConfig.color }}
                                    role="option"
                                    aria-selected={isActive}
                                    tabIndex={-1}
                                >
                                    <div className="role-option-icon">
                                        <img src={roleConfig.icon} alt="" />
                                    </div>
                                    <div className="role-option-info">
                                        <span className="role-option-label">{roleConfig.label}</span>
                                        <span className="role-option-description">{roleConfig.description}</span>
                                    </div>
                                    <div className="role-option-status">
                                        {isActive ? (
                                            <span className="active-badge">
                                                <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
                                                    <path d="M11.667 3.5L5.25 9.917L2.333 7" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                                                </svg>
                                                Current
                                            </span>
                                        ) : (
                                            <span className="switch-hint">
                                                <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
                                                    <path d="M5.25 2.333L9.917 7L5.25 11.667" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                                                </svg>
                                            </span>
                                        )}
                                    </div>
                                </button>
                            );
                        })}
                    </div>

                    <div className="dropdown-footer">
                        <span className="footer-hint">
                            <kbd>Enter</kbd> to select · <kbd>Esc</kbd> to close
                        </span>
                    </div>
                </div>
            )}
        </div>
    );
};

export default RoleSwitcher;
